import { ApiError, logError } from "./errors";
import { getGlobalSessionInfo, sendLogoutRequest } from "../api";
import { Action, ActionType, MctToastType } from "../@types";
import { getSlsxSignoutUrl } from "./loginHelpers";

interface LogoutParams {
  apiExpired?: boolean;
  clientExpired?: boolean;
  dispatch: React.Dispatch<Action>;
  debugFn?: (message: string) => void;
  onLogout?: () => void;
}

/**
 * - Relies on `medicare.gov/sso/signout` to end global session
 * - Also resets application state
 * - `apiExpired` controls logging out MCTAPI at `/beneinfo/logout` (defaults to `false`)
 *     - If `true`, no MCTAPI logout call is made
 *     - If `false`, an attempt is made to log out MCTAPI
 * - `clientExpired` controls the toast type on redirect from logout (defaults to `false`)
 *     - If `true`, the user will see a "Session Expired" toast after logout
 *     - If `false`, the user will see a "Logged Out" toast after logout
 */
export const logout = async ({
  apiExpired = false, // @TODO - is this param even necessary, do we always want to call `/beneinfo/logout`?
  clientExpired = false,
  dispatch,
  debugFn = () => {},
  onLogout = () => {},
}: LogoutParams): Promise<void> => {
  const queriesToAppend = {
    mctt: clientExpired
      ? MctToastType.SESSION_EXPIRED
      : MctToastType.LOGGED_OUT,
  };
  debugFn(
    `logout called with values apiExpired: ${apiExpired}, clientExpired: ${clientExpired}.`
  );
  // This should not block further logout actions, so is safe to do in pretty much
  // any circumstance
  if (!apiExpired) {
    try {
      debugFn(`logout calling sendLogoutRequest`);
      await sendLogoutRequest();
    } catch (_e) {
      const e = _e as ApiError;
      debugFn(`sendLogoutRequest failed with error: ${e?.message}`);
      logError("Could not log out user (GlobalSessionHandler)", e);
    }
  }
  debugFn(
    `in logout, set showInactiveDialog to false, toast message (${
      clientExpired ? "session expired" : "logged out"
    }), dispatch RESET_STATE`
  );
  // @TODO - Evaluate whether the order of this logic - particularly the
  // fact that the try/catch operates independently of the code below it - needs
  // to be modified at all to take into account a possible `sendLogoutRequest` failure
  onLogout();
  // history.push(pushToHistory);
  dispatch({ type: ActionType.RESET_STATE });
  const signoutUrl = getSlsxSignoutUrl({
    queriesToAppend,
    redirectLocalToTest: true,
  });
  console.debug(`🌝 signoutUrl: ${signoutUrl}`);
  window.location.replace(signoutUrl);
};

export const getIsSlsxAuthenticated = async () => {
  const [res, error] = await getGlobalSessionInfo()
    .then(res => [res, null])
    .catch(error => [null, error]);
  return error || res === undefined ? false : true;
};

export const getBeneSessionInfo = async (
  defaultTimeUntilSessionExpiresMs: number
) => {
  const info = {
    sessionExpirationInMs: 0,
    idleTimeoutMs: 0,
    forceLogoutTimeoutMs: 0,
    loggedOut: false,
  };

  try {
    const session = await getGlobalSessionInfo();

    if (session) {
      const { exp: sessionExpirationInSeconds } = session;
      const sessionExpirationInMs = sessionExpirationInSeconds * 1000;
      const timeUntilSessionExpiresMs = sessionExpirationInMs - Date.now();
      info.sessionExpirationInMs = sessionExpirationInMs;

      if (timeUntilSessionExpiresMs > defaultTimeUntilSessionExpiresMs) {
        info.idleTimeoutMs =
          timeUntilSessionExpiresMs - defaultTimeUntilSessionExpiresMs;
      } else {
        info.forceLogoutTimeoutMs = timeUntilSessionExpiresMs;
      }
    }
  } catch (err) {
    const e = err as ApiError;
    const message = e.message || "";
    logError(`Error calling /api/sls/session/info: ${message}`, e);
    info.loggedOut = true;
  }

  return info;
};
